import React from 'react'
import InputAdornment from '@material-ui/core/InputAdornment'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import { FormControl } from '@material-ui/core'
import { SelectProps } from '@material-ui/core/Select/Select'
import { isRtl } from '../../i18nInit'

export interface YumSelectProps extends SelectProps {
  label?: string
  startIcon?: any
  StartIconComponent?: any
  startInactiveIcon?: any
  StartInactiveIconComponent?: any
  sideMargin?: number
  verticalPadding?: number
}

let yumSelectIdCounter = 0

class YumSelect extends React.Component<YumSelectProps> {
  private readonly selectId: string

  constructor(props: any) {
    super(props)
    this.state = {
      shrink: false, // this is used to shrink/unshrink the label
    }
    yumSelectIdCounter += 1
    this.selectId = `yum-select-${yumSelectIdCounter}`
  }

  shrinkLabel = (event: any) => {
    const { onFocus } = this.props
    this.setState({ shrink: true })
    onFocus && onFocus(event) // let the child do its thing
  }

  unShrinkLabel = (event: any) => {
    const { onBlur } = this.props
    if (event.target.value.length === 0 && !event.target.validity.badInput) {
      this.setState({ shrink: false }) // gotta make sure the input is empty before shrinking the label
    }
    onBlur && onBlur(event) // let the child do its thing
  }

  // MUI v4's InputBase filters aria-label/title off the native <select> (only `id` and
  // `style` from inputProps survive). Write aria-label straight onto the DOM node after
  // render so axe can resolve an accessible name regardless of MUI's prop forwarding.
  componentDidMount() {
    this.applyAriaLabelToNativeSelect()
  }
  componentDidUpdate() {
    this.applyAriaLabelToNativeSelect()
  }
  private applyAriaLabelToNativeSelect() {
    const callerInputProps = (this.props.inputProps as any) || {}
    const ariaLabel =
      callerInputProps['aria-label'] ||
      (this.props as any)['aria-label'] ||
      (typeof this.props.label === 'string' ? this.props.label : undefined)
    if (!ariaLabel) return
    const id = callerInputProps.id || this.selectId
    const el = document.getElementById(id)
    if (el) el.setAttribute('aria-label', ariaLabel)
  }

  render() {
    const {
      children,
      label,
      startIcon,
      StartIconComponent,
      startInactiveIcon,
      StartInactiveIconComponent,
      value,
      sideMargin,
      verticalPadding,
      inputProps: callerInputPropsRaw,
      ...other
    } = this.props
    const hasStartIcon = startIcon || StartIconComponent
    const StartIcon = startIcon ? startIcon : null
    const StartInactiveIcon = startInactiveIcon ? startInactiveIcon : null

    // @ts-ignore
    const noEmptyChoice = children && React.Children.count(children) > 0 ? children[0]?.props?.value !== '' : false

    // @ts-ignore
    const shrink = noEmptyChoice || (value !== null && value !== undefined && value !== '') || this.state.shrink

    const callerInputProps = callerInputPropsRaw || {}
    const nativeSelectId = callerInputProps.id || this.selectId
    // Read aria-label from either the caller's inputProps or a top-level aria-label so
    // either API works; a native <select> without an accessible name fails axe `select-name`.
    const callerAriaLabel = callerInputProps['aria-label'] || (other as any)['aria-label']

    // When no visible <InputLabel> is rendered but the caller provided an aria-label,
    // emit a visually-hidden <label htmlFor=...> so the native <select> has a guaranteed
    // accessible name, independent of MUI v4's inputProps cloning path.
    const hiddenLabelStyle: React.CSSProperties = {
      position: 'absolute',
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: 'hidden',
      clip: 'rect(0,0,0,0)',
      whiteSpace: 'nowrap',
      border: 0,
    }

    return (
      <FormControl margin="none" variant="filled" style={{ width: '100%' }}>
        {!label && callerAriaLabel && (
          <label htmlFor={nativeSelectId} style={hiddenLabelStyle}>
            {callerAriaLabel}
          </label>
        )}
        {label && (
          <InputLabel
            htmlFor={nativeSelectId}
            shrink={shrink}
            style={
              hasStartIcon
                ? isRtl
                  ? {
                      marginRight: sideMargin ? sideMargin : '33px',
                    }
                  : {
                      marginLeft: sideMargin ? sideMargin : '33px',
                    }
                : {}
            }
          >
            {label}
          </InputLabel>
        )}
        <Select
          fullWidth={true}
          native
          inputProps={{
            ...callerInputProps,
            id: nativeSelectId,
            'aria-label': callerAriaLabel || (typeof label === 'string' ? label : undefined),
            // `title` as a third fallback accessible-name path, since MUI v4 has been seen
            // to strip aria-label from inputProps on this project.
            ...(callerAriaLabel ? { title: callerAriaLabel, 'data-a11y-label': callerAriaLabel } : {}),
            style: {
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              color: 'inherit',
              textTransform: 'inherit',
              paddingTop: label ? '18px' : `${verticalPadding || 12}px`,
              paddingBottom: label ? '4px' : `${verticalPadding || 12}px`,

              marginLeft: isRtl ? 0 : hasStartIcon ? '-42px' : 0,
              paddingLeft: isRtl ? '19px' : hasStartIcon ? '42px' : '10px',

              marginRight: isRtl ? (hasStartIcon ? '-42px' : 0) : 0,
              paddingRight: isRtl ? (hasStartIcon ? '42px' : '10px') : '19px',
              ...(callerInputProps.style || {}),
            },
          }}
          disableUnderline
          startAdornment={
            StartIcon ? (
              <InputAdornment position={'start'} style={{ marginTop: 0 }}>
                {StartInactiveIcon && !shrink ? <StartInactiveIcon /> : <StartIcon />}
              </InputAdornment>
            ) : StartIconComponent ? (
              <InputAdornment position={'start'} style={{ marginTop: 0 }}>
                {StartInactiveIconComponent && !shrink ? StartInactiveIconComponent : StartIconComponent}
              </InputAdornment>
            ) : (
              ''
            )
          }
          onFocus={this.shrinkLabel}
          onBlur={this.unShrinkLabel}
          value={value}
          {...other}
        >
          {children}
        </Select>
      </FormControl>
    )
  }
}

export default YumSelect
